/**
 * The recovery window, as the interface has to describe it.
 *
 * Thirty days, swept hourly, after which the record and its bytes are gone for
 * good (ADR 000055). "Not gone for good" is true for thirty days and false on
 * the thirty-first, and a screen that says only the first half leaves somebody
 * to find out the hard way.
 *
 * The length is duplicated from `recoveryWindow` in the worker rather than
 * served: it is one constant that changes with an argument, and a browser
 * asking the API how long its own grace period is would be a round trip to
 * learn something the ADR fixed.
 */
export const RECOVERY_WINDOW_DAYS = 30;

/** How close to expiry counts as urgent, in days. */
const URGENT_WITHIN_DAYS = 3;

export interface Remaining {
  /** Whole days left, floored, never below zero. */
  days: number;
  /** What to show: "3 days left", "today", "any moment now". */
  label: string;
  /** Worth drawing attention to rather than listing quietly. */
  urgent: boolean;
  /** Past the window: the sweep has not reached it yet, but it is not coming back. */
  expired: boolean;
}

/**
 * How long is left before a deleted thing is swept.
 *
 * Rounded up, so something deleted ten days ago into a thirty day window reads
 * "20 days left" rather than "19": flooring is arithmetically right and looks
 * like an off-by-one to the person reading it, because the elapsed seconds
 * since the page loaded are not something they can see.
 *
 * The overstatement that rounding up would otherwise cause is handled at the
 * only place it is dangerous. Under a day says so outright rather than "1 day
 * left", so the last hours never read as a whole day.
 */
export function remainingIn(
  deletedAt: string | null | undefined,
  now = new Date(),
): Remaining | null {
  if (!deletedAt) return null;

  const deleted = Date.parse(deletedAt);
  if (Number.isNaN(deleted)) return null;

  const msLeft = deleted + RECOVERY_WINDOW_DAYS * 24 * 60 * 60 * 1000 - now.getTime();
  if (msLeft <= 0) {
    return { days: 0, label: "any moment now", urgent: true, expired: true };
  }

  const day = 24 * 60 * 60 * 1000;
  if (msLeft < day) {
    return { days: 0, label: "gone today", urgent: true, expired: false };
  }

  const days = Math.ceil(msLeft / day);
  return {
    days,
    label: `${days} ${days === 1 ? "day" : "days"} left`,
    urgent: days <= URGENT_WITHIN_DAYS,
    expired: false,
  };
}
